import { createContext, useContext, type ReactNode } from "react";
import { Outlet, useMatch, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ApiErrorException } from "@/api/client";
import { useProject } from "@/api/hooks";
import type { Project } from "@/api/types/Project";
import { ProjectLogo } from "@/components/ProjectLogo";
import { Chip, LoadError, Notice, PageSpinner } from "@/components/ui";
import { NotFoundPage } from "@/pages/NotFoundPage";

const ProjectContext = createContext<Project | null>(null);

/** The project of the current /p/:key page. */
export function useProjectContext(): Project {
  const p = useContext(ProjectContext);
  if (!p) throw new Error("useProjectContext() used outside a project page");
  return p;
}

/** Loads the project (from the `:key` route param unless `projectKey` names one) and provides it to `children`. */
export function ProjectGate({ children, projectKey }: { children: ReactNode; projectKey?: string }) {
  const params = useParams();
  const project = useProject(projectKey ?? params.key);
  if (project.isPending) return <PageSpinner />;
  if (project.isError) {
    const status = project.error instanceof ApiErrorException ? project.error.status : 0;
    if (status === 404 || status === 403) return <NotFoundPage />;
    return <LoadError onRetry={() => void project.refetch()} />;
  }
  return <ProjectContext.Provider value={project.data}>{children}</ProjectContext.Provider>;
}

/** Every /p/:key page: the project header (except on task and bug pages, which have their own) and content. */
export function ProjectLayout() {
  return (
    <ProjectGate>
      <div className="flex flex-col gap-5 p-6">
        <ProjectHeader />
        <Outlet />
      </div>
    </ProjectGate>
  );
}

/** Scrum pages (sprints, backlog, poker, standup) exist only while the project has scrum on. */
export function RequireScrum() {
  return useProjectContext().scrum_enabled ? <Outlet /> : <NotFoundPage />;
}

function ProjectHeader() {
  const { t } = useTranslation();
  const p = useProjectContext();
  const task = useMatch("/p/:key/tasks/:taskKey/*");
  const onTask = !!task && task.params.taskKey?.toUpperCase() !== "REGRESSION";
  const onBug = useMatch("/p/:key/bugs/:n");
  if (onTask || onBug) return null;

  return (
    <>
      <header className="flex flex-wrap items-center gap-3">
        <ProjectLogo project={p} size={34} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 text-2xl leading-tight font-semibold tracking-tight">{p.name}</h1>
            <span className="font-mono text-sm text-muted">{p.key}</span>
            {p.archived && <Chip tone="outline">{t("projects.archived")}</Chip>}
          </div>
        </div>
      </header>
      {p.archived && <Notice>{t("overview.archivedNotice")}</Notice>}
    </>
  );
}
